import { supabase } from "@/integrations/supabase/client";
import { trackEvent } from "@/lib/audit";
import type { Voucher } from "./voucherEngine";

interface SendOpts {
  isResend?: boolean;
  recipientEmail?: string;
}

/**
 * Checks whether real email delivery is enabled. Cached for 30s to avoid
 * hammering the DB. When the flag is missing or false, we operate in standby.
 */
let _flagCache: { enabled: boolean; at: number } | null = null;
export async function isEmailDeliveryEnabled(): Promise<boolean> {
  if (_flagCache && Date.now() - _flagCache.at < 30_000) return _flagCache.enabled;
  try {
    const { data } = await supabase
      .from("feature_flags")
      .select("enabled")
      .eq("key", "reward_email_delivery_enabled")
      .maybeSingle();
    const enabled = !!data?.enabled;
    _flagCache = { enabled, at: Date.now() };
    return enabled;
  } catch {
    return false;
  }
}

/**
 * Sends a voucher email via the Lovable Emails transactional pipeline.
 * When the `reward_email_delivery_enabled` feature flag is false (default),
 * the system operates in standby — vouchers are still issued, but no real
 * email is sent and a `reward_voucher_email_pending_configuration` audit
 * event is logged. Flip the flag to true once the sender domain is verified.
 */
export async function sendVoucherEmail(voucher: Voucher, opts: SendOpts = {}): Promise<{ ok: boolean; reason?: string }> {
  const recipient = opts.recipientEmail ?? voucher.recipient_email ?? null;
  if (!recipient) return { ok: false, reason: "no_recipient" };

  const enabled = await isEmailDeliveryEnabled();
  if (!enabled) {
    void trackEvent("reward_voucher_email_pending_configuration", {
      code: voucher.code,
      to: recipient,
      isResend: !!opts.isResend,
    });
    return { ok: false, reason: "delivery_disabled" };
  }

  const meta = (voucher.metadata as { reward_name?: string } | null) ?? null;
  const rewardName = meta?.reward_name ?? voucher.code;
  const partnerName = voucher.partner?.name ?? "VerseSport™";
  const expiresAt = voucher.expires_at;

  try {
    const res = await fetch("/api/public/send-voucher-email", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        voucherId: voucher.id,
        recipientEmail: recipient,
        code: voucher.code,
        qrPayload: voucher.qr_payload,
        rewardName,
        partnerName,
        partnerLogo: voucher.partner?.logo_url ?? null,
        expiresAt,
        isResend: !!opts.isResend,
      }),
    });
    if (!res.ok) {
      void trackEvent("reward_voucher_email_failed", { code: voucher.code, status: res.status });
      return { ok: false, reason: `http_${res.status}` };
    }
    const stamp = new Date().toISOString();
    if (opts.isResend) {
      await supabase
        .from("reward_vouchers")
        .update({
          delivery_email_sent_at: stamp,
          delivery_email_resent_count: (voucher.delivery_email_resent_count ?? 0) + 1,
        })
        .eq("id", voucher.id);
      void trackEvent("reward_voucher_email_resent", { code: voucher.code, to: recipient });
    } else {
      await supabase
        .from("reward_vouchers")
        .update({ delivery_email_sent_at: stamp })
        .eq("id", voucher.id);
      void trackEvent("reward_voucher_email_sent", { code: voucher.code, to: recipient });
      void trackEvent("reward_voucher_delivered", { code: voucher.code, channel: "email" });
    }
    return { ok: true };
  } catch (e) {
    console.warn("[voucher-delivery] email send failed", e);
    void trackEvent("reward_voucher_email_failed", { code: voucher.code, error: String(e) });
    return { ok: false, reason: "network" };
  }
}
